import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Loader2, ShoppingCart, Share2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, fmt } from "../lib/api";
import { itemUnitPrice } from "../lib/pricing";
import { itemName, itemSizeLabel } from "../lib/localize";
import { SEO } from "../components/SEO";
import { FadeUp } from "../components/motion";
import { ShareButtons } from "../components/ShareButtons";
import { useCart } from "../context/CartContext";
import { useLang } from "../i18n/LanguageContext";

// Panier partagé par un tiers (/panier-partage/{token}) : consultation + « tout ajouter
// à mon panier ». Ex. un entraîneur prépare les trophées et partage le lien aux parents.
export default function SharedCart() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { add } = useCart();
  const { lang, t, ts } = useLang();
  const [data, setData] = useState(null);  // null = chargement, false = introuvable

  useEffect(() => {
    api.get(`/carts/share/${token}`)
      .then((r) => setData(r.data))
      .catch(() => setData(false));
  }, [token]);

  const items = data?.items || [];
  const total = useMemo(() => items.reduce((s, it) =>
    s + itemUnitPrice(it) * it.qty, 0), [items]);

  const addAll = () => {
    items.forEach((it) => add({ ...it }));
    toast.success(t("shared_cart.added"));
    navigate("/panier");
  };

  if (data === false) {
    return (
      <div className="pt-40 pb-28 text-center px-6" data-testid="shared-cart-not-found">
        <h1 className="font-serif text-4xl mb-6">{t("shared_cart.not_found")}</h1>
        <Link to="/boutique" className="text-gold hover:text-champagne transition-colors duration-300 inline-flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" /> {t("shared_cart.go_shop")}
        </Link>
      </div>
    );
  }
  if (!data) {
    return <div className="flex items-center justify-center py-40"><Loader2 className="w-8 h-8 animate-spin text-gold" /></div>;
  }

  return (
    <div className="pt-32 pb-28 max-w-4xl mx-auto px-6 lg:px-10" data-testid="shared-cart-page">
      <SEO title={`${ts("shared_cart.title")} | Mr Trophée`} description={ts("shared_cart.intro")} path={`/panier-partage/${token}`} noindex />
      <FadeUp>
        <p className="text-gold tracking-[0.3em] uppercase text-xs mb-4 flex items-center gap-2">
          <Share2 className="w-4 h-4" /> {t("shared_cart.kicker")}
        </p>
        <h1 className="font-serif text-4xl md:text-5xl leading-tight mb-4" data-testid="shared-cart-title">{t("shared_cart.title")}</h1>
        <p className="text-ivory/60 text-sm leading-relaxed max-w-2xl mb-10">{t("shared_cart.intro")}</p>
      </FadeUp>

      <FadeUp delay={0.08}>
        <div className="bg-surface border border-ivory/10 rounded-xl p-6 mb-8">
          <div className="space-y-4 mb-6">
            {items.map((it, i) => {
              const unit = itemUnitPrice(it);
              return (
                <div key={i} data-testid={`shared-item-${i}`} className="flex items-center gap-4 border-b border-ivory/5 pb-4 last:border-0 last:pb-0">
                  {it.image && (
                    <div className="w-16 h-16 bg-white rounded-lg p-1 shrink-0">
                      <img src={it.image} alt="" className="w-full h-full object-contain" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{itemName(it, lang)}</p>
                    <p className="text-xs text-ivory/60">
                      {[itemSizeLabel(it, lang),
                        it.inscription_type && it.inscription_type !== "aucune" ? t("shared_cart.with_inscription") : null,
                        `× ${it.qty}`].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                  <p className="text-sm text-gold font-semibold shrink-0">{fmt(unit * it.qty)}</p>
                </div>
              );
            })}
          </div>
          <div className="flex justify-between items-center border-t border-ivory/10 pt-4">
            <span className="text-xs uppercase tracking-widest text-ivory/65">{t("shared_cart.subtotal")}</span>
            <span className="font-serif text-2xl text-gold" data-testid="shared-cart-total">{fmt(total)}</span>
          </div>
          <p className="text-[11px] text-ivory/55 mt-2">{t("shared_cart.note")}</p>
        </div>
      </FadeUp>

      <FadeUp delay={0.12}>
        <button onClick={addAll} data-testid="shared-cart-add-all"
          className="btn-gold-shine w-full inline-flex items-center justify-center gap-2.5 text-ink font-bold px-8 py-4 rounded-full text-sm mb-8 transition-shadow duration-300">
          <ShoppingCart className="w-5 h-5" /> {t("shared_cart.add_all")}
        </button>
        <ShareButtons title={ts("shared_cart.title")} label={t("footer.share_page")} />
      </FadeUp>
    </div>
  );
}
